import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { DEFAULT_COUNTRY_CODE } from "@unreserved/constants";
import { useDebounce } from "@unreserved/hooks/use-debounce";
import {
  type PublicInspectionFormData,
  publicInspectionSchema,
} from "@unreserved/schemas/enquiries/public-inspection-schema";
import type { PropertyListItemDto } from "@unreserved/sdk";
import { Label } from "@unreserved/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@unreserved/ui/components/select";
import { auStateTimezoneUtils, getPropertyTodayPickerDate } from "@unreserved/utils";
import { Controller, useForm } from "react-hook-form";

import { useEnquiryModalDropdown } from "@/app/_components/my-enquiries/modal/enquiry-modal-dropdown-context";
import {
  buildTimeSlots,
  parseTimeSlotId,
} from "@/app/_components/my-enquiries/modal/enquiry-util-func";
import { PropertyAddressInput } from "@/app/_components/my-enquiries/modal/property-address-input";
import { DateInput } from "@/app/_components/shared";
import { Input } from "@/app/_components/shared/input";
import { PhoneInput } from "@/app/_components/shared/phone-input";
import { getPropertyListingState } from "@/app/_libs/utils/my-enquiries/book-inspection-utils";
import { useCreateInspection, usePropertiesForEnquiry } from "@/hooks/my-enquiry/use-my-enquiry";
import { useAuthStore } from "@/stores/auth-store";

const TIME_SLOTS = buildTimeSlots();

export const PrivateInspectionForm = () => {
  const user = useAuthStore((s) => s.user);
  const {
    setPropertyDropdownOpen,
    setDateTimeVisible,
    setIsSubmitting,
    setSuccessConfig,
    preselectedListingId,
    preselectedListingState,
  } = useEnquiryModalDropdown();
  const [selectedProperty, setSelectedProperty] = useState<PropertyListItemDto | null>(null);
  const timeZone = auStateTimezoneUtils.getTimezoneForState(
    preselectedListingState ?? getPropertyListingState(selectedProperty),
  );
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [propertySearchText, setPropertySearchText] = useState("");
  const debouncedSearch = useDebounce(propertySearchText, 300);
  const createInspection = useCreateInspection();

  useEffect(() => {
    setIsSubmitting(createInspection.isPending);
  }, [createInspection.isPending, setIsSubmitting]);
  const {
    data: propertiesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePropertiesForEnquiry({
    privateInspectionAllowed: "yes",
    propertyTitle: debouncedSearch || undefined,
  });

  const userEmail = user?.email != null ? String(user.email) : "";
  const userName = user?.fullName != null ? String(user.fullName) : "";
  const userPhoneNumber = user?.phoneNumber != null ? String(user.phoneNumber) : "";
  const properties = useMemo(
    () => propertiesData?.pages?.flatMap((page) => page.data?.properties ?? []) ?? [],
    [propertiesData],
  );

  const propertySuggestions = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        displayLabel: p.title != null && typeof p.title === "string" ? p.title : p.id,
        data: p as PropertyListItemDto,
      })),
    [properties],
  );

  const {
    register: registerField,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, touchedFields, submitCount },
  } = useForm<PublicInspectionFormData>({
    resolver: zodResolver(publicInspectionSchema),
    defaultValues: {
      email: "",
      name: "",
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: "",
      propertyAddress: "",
      selectedDate: getPropertyTodayPickerDate(timeZone),
      selectedTime: "",
    },
    mode: "onChange",
  });

  const selectedTime = watch("selectedTime");
  const countryCode = watch("countryCode");

  useEffect(() => {
    if (userEmail) setValue("email", userEmail);
    if (userName) setValue("name", userName);
  }, [userEmail, userName, setValue]);

  useEffect(() => {
    if (selectedProperty === null) {
      setValue("selectedDate", getPropertyTodayPickerDate(timeZone));
      setValue("selectedTime", "");
    }
  }, [selectedProperty, setValue, timeZone]);

  useEffect(() => {
    setDateTimeVisible(selectedProperty !== null);
  }, [selectedProperty, setDateTimeVisible]);

  useEffect(() => {
    if (preselectedListingId && properties.length > 0 && !selectedProperty) {
      const match = properties.find(
        (p) => p.id === preselectedListingId || String(p.listingId) === preselectedListingId,
      );
      if (match) {
        setSelectedProperty(match);
        setValue("propertyAddress", typeof match.title === "string" ? match.title : match.id);
      }
    }
  }, [preselectedListingId, properties, selectedProperty, setValue]);

  const onSubmit = (data: PublicInspectionFormData) => {
    if (!selectedProperty || !data.selectedTime) return;
    const listingId =
      typeof selectedProperty.listingId === "string"
        ? selectedProperty.listingId
        : String(selectedProperty.listingId ?? "");
    if (!listingId) return;
    const parsed = parseTimeSlotId(data.selectedTime);
    if (!parsed) return;
    const [hour, minute] = parsed;
    const { year, month, day } = auStateTimezoneUtils.getWallDateParts(data.selectedDate);
    const start = auStateTimezoneUtils.zonedTimeToUtc({ year, month, day, hour, minute }, timeZone);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    const inspectionStartTime = start.toISOString();
    const inspectionEndTime = end.toISOString();

    createInspection.mutate(
      {
        email: data.email,
        name: data.name,
        countryCode: data.phoneNumber ? data.countryCode : undefined,
        phoneNumber: data.phoneNumber || undefined,
        listingId,
        inspectionType: "private",
        inspectionStartTime,
        inspectionEndTime,
      },
      {
        onSuccess: () => {
          setSuccessConfig({
            title: "Private Inspection Booking Done !!",
            subheading:
              "Awaiting Vendor to confirm his availability, you'll be notified via mail once responded",
            primaryButtonRedirect: "dashboard",
          });
        },
      },
    );
  };

  return (
    <form id="private-inspection-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-5">
        {/* Email */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Email ID
            {/* <span className="text-error-500">*</span> */}
          </Label>
          <Input
            type="email"
            placeholder="Enter here"
            className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border px-3 text-sm focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
            {...registerField("email")}
            aria-invalid={errors.email ? "true" : "false"}
            disabled={!!userEmail}
          />
          {errors.email && <p className="text-error-600 text-sm">{errors.email.message}</p>}
        </div>

        {/* Name */}
        {!userName && (
          <div className="space-y-1.5">
            <Label className="text-typography-750 text-sm font-medium">Full Name</Label>
            <Input
              type="text"
              placeholder="Enter here"
              className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border px-3 text-sm focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
              {...registerField("name")}
              aria-invalid={errors.name ? "true" : "false"}
            />
            {errors.name && <p className="text-error-600 text-sm">{errors.name.message}</p>}
          </div>
        )}

        {/* Phone */}
        {!userPhoneNumber && (
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <PhoneInput
                label="Phone Number"
                value={field.value}
                onChange={field.onChange}
                countryCode={countryCode}
                onCountryCodeChange={(code) => setValue("countryCode", code)}
                placeholder="Enter phone number"
                error={errors.phoneNumber?.message}
              />
            )}
          />
        )}

        {/* Property */}
        <div className={`space-y-1.5 ${isPropertyDropdownOpen ? "mb-36" : ""}`}>
          <Label className="text-typography-750 text-sm font-medium">
            Select Property
            {/* <span className="text-error-500">*</span> */}
          </Label>
          <Controller
            control={control}
            name="propertyAddress"
            render={({ field }) => {
              const selectedDisplayLabel =
                selectedProperty != null
                  ? typeof selectedProperty.title === "string"
                    ? selectedProperty.title
                    : selectedProperty.id
                  : "";
              return (
                <PropertyAddressInput<PropertyListItemDto>
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    setPropertySearchText(value);
                    if (value === "" || value !== selectedDisplayLabel) {
                      setSelectedProperty(null);
                    }
                  }}
                  placeholder="Search or select property"
                  externalSuggestions={propertySuggestions}
                  onDropdownOpenChange={(open, count) => {
                    setPropertyDropdownOpen(open, count);
                    setIsPropertyDropdownOpen(open);
                  }}
                  scrollToBottomWhenEmpty={true}
                  onSelectSuggestion={(item) => {
                    setSelectedProperty(item.data as PropertyListItemDto);
                    field.onChange(item.displayLabel);
                    setPropertySearchText("");
                  }}
                  onEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      void fetchNextPage();
                    }
                  }}
                  loadingMore={isFetchingNextPage}
                  hasMore={hasNextPage}
                  loading={isLoading}
                />
              );
            }}
          />

          {errors.propertyAddress && (touchedFields.propertyAddress || submitCount > 0) && (
            <p className="text-error-600 text-sm">{errors.propertyAddress.message}</p>
          )}
        </div>

        {selectedProperty !== null && (
          <>
            {/* Date */}
            <div>
              <Controller
                control={control}
                rules={{ required: "Select date is required" }}
                name="selectedDate"
                render={({ field }) => (
                  <DateInput
                    label={
                      <>
                        <span>Select Date</span> {/* <span className="text-red-600">*</span> */}
                      </>
                    }
                    placeholder="Select"
                    variant="default"
                    value={field.value}
                    onChange={field.onChange}
                    disablePastDates={true}
                    className="h-9 cursor-pointer rounded"
                    id="selectedDate"
                    name="selectedDate"
                    error={errors.selectedDate?.message}
                    aria-invalid={errors.selectedDate ? "true" : "false"}
                  />
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-typography-750 text-sm font-medium">
                Select Time
                {/* <span className="text-error-500">*</span> */}
              </Label>
              <Select
                value={selectedTime}
                onValueChange={(value) =>
                  setValue("selectedTime", value, { shouldValidate: true })
                }>
                <SelectTrigger className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border bg-white! px-3 text-sm focus-visible:ring-4 focus-visible:outline-none">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent
                  side="bottom"
                  align="start"
                  className="text-typography-900 max-h-[200px] w-[calc(var(--radix-select-trigger-width)/2)] overflow-y-auto bg-white! **:data-[slot=select-scroll-down-button]:hidden **:data-[slot=select-scroll-up-button]:hidden">
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem
                      key={slot.id}
                      value={slot.id}
                      className="text-typography-900 data-highlighted:bg-primary-700 justify-start text-left data-highlighted:text-white">
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.selectedTime && (
                <p className="text-error-600 text-sm">{errors.selectedTime.message}</p>
              )}
            </div>
          </>
        )}
      </div>
    </form>
  );
};
